import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Search, Filter, Check, X, Eye, Download, FileText, User, Bus, Clock, AlertTriangle, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { getAllChecks, getCheckDetail, getDrivers, getAdminVehicles } from '../../lib/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

export default function ChecksPage() {
  const [checks, setChecks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [showFullReport, setShowFullReport] = useState(false);

  // Filters
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [selectedVehicle, setSelectedVehicle] = useState('all');
  const [issuesOnly, setIssuesOnly] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadChecks();
  }, [dateFrom, dateTo, selectedDriver, selectedVehicle, issuesOnly]);

  const loadInitialData = async () => {
    try {
      const [driversRes, vehiclesRes] = await Promise.all([
        getDrivers(),
        getAdminVehicles()
      ]);
      setDrivers(driversRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      console.error('Failed to load initial data');
    }
  };

  const loadChecks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = format(dateFrom, 'yyyy-MM-dd');
      if (dateTo) params.date_to = format(dateTo, 'yyyy-MM-dd');
      if (selectedDriver !== 'all') params.driver_id = selectedDriver;
      if (selectedVehicle !== 'all') params.vehicle_id = selectedVehicle;
      if (issuesOnly) params.has_issues = true;

      const response = await getAllChecks(params);
      setChecks(response.data);
    } catch (error) {
      toast.error('Failed to load checks');
    } finally {
      setLoading(false);
    }
  };

  const viewFullReport = async (check) => {
    try {
      const response = await getCheckDetail(check.id);
      setSelectedCheck(response.data);
      setShowFullReport(true);
    } catch (error) {
      toast.error('Failed to load check details');
    }
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'dd MMM yyyy, HH:mm');
  };

  const formatDateLong = (dateString) => {
    return format(new Date(dateString), 'EEEE, dd MMMM yyyy \'at\' HH:mm');
  };

  const clearFilters = () => {
    setDateFrom(null);
    setDateTo(null);
    setSelectedDriver('all');
    setSelectedVehicle('all');
    setIssuesOnly(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'fail':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <MinusCircle className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pass':
        return <Badge className="bg-green-100 text-green-800 font-semibold">PASS</Badge>;
      case 'fail':
        return <Badge className="bg-red-100 text-red-800 font-semibold">FAIL</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600 font-semibold">N/A</Badge>;
    }
  };

  // Count pass/fail/na
  const getChecklistSummary = (checklist) => {
    const summary = { pass: 0, fail: 0, na: 0 };
    checklist.forEach(item => {
      if (item.status === 'pass') summary.pass++;
      else if (item.status === 'fail') summary.fail++;
      else summary.na++;
    });
    return summary;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="checks-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-blue-900">Vehicle Checks</h1>
          <p className="text-slate-500">View all submitted checks and full reports</p>
        </div>

        {/* Filters */}
        <Card className="border-slate-200" data-testid="filters-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Date From */}
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">From Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left" data-testid="date-from-btn">
                      {dateFrom ? format(dateFrom, 'dd/MM/yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateFrom}
                      onSelect={setDateFrom}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Date To */}
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">To Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left" data-testid="date-to-btn">
                      {dateTo ? format(dateTo, 'dd/MM/yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateTo}
                      onSelect={setDateTo}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Driver Filter */}
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Driver</label>
                <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                  <SelectTrigger data-testid="driver-filter">
                    <SelectValue placeholder="All Drivers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Drivers</SelectItem>
                    {drivers.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vehicle Filter */}
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Vehicle</label>
                <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                  <SelectTrigger data-testid="vehicle-filter">
                    <SelectValue placeholder="All Vehicles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vehicles</SelectItem>
                    {vehicles.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.registration}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Issues Only Toggle */}
              <div className="flex flex-col justify-end">
                <Button
                  variant={issuesOnly ? 'default' : 'outline'}
                  onClick={() => setIssuesOnly(!issuesOnly)}
                  className={issuesOnly ? 'bg-red-600 hover:bg-red-700' : ''}
                  data-testid="issues-filter-btn"
                >
                  Issues Only
                </Button>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters-btn">
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="border-slate-200" data-testid="checks-results">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">
              Results ({checks.length} checks)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="spinner" />
              </div>
            ) : checks.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                No checks found matching your filters
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date/Time</th>
                      <th>Driver</th>
                      <th>Vehicle</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Full Report</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checks.map((check, idx) => (
                      <tr key={check.id} data-testid={`check-row-${idx}`}>
                        <td className="font-mono text-sm">{formatDate(check.submitted_at)}</td>
                        <td>
                          <div className="font-medium">{check.driver_name}</div>
                          <div className="text-xs text-slate-500">#{check.driver_badge}</div>
                        </td>
                        <td className="font-mono font-semibold">{check.vehicle_registration}</td>
                        <td>
                          <Badge variant="outline" className="capitalize">
                            {check.vehicle_type}
                          </Badge>
                        </td>
                        <td>
                          {check.has_issues ? (
                            <Badge variant="destructive" className="gap-1">
                              <X className="w-3 h-3" />
                              Issues
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800 gap-1">
                              <Check className="w-3 h-3" />
                              Clear
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => viewFullReport(check)}
                            className="bg-blue-900 hover:bg-blue-800 gap-1"
                            data-testid={`view-report-${idx}`}
                          >
                            <FileText className="w-4 h-4" />
                            View Report
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Full Report Dialog */}
        <Dialog open={showFullReport} onOpenChange={setShowFullReport}>
          <DialogContent className="max-w-3xl max-h-[90vh] p-0">
            {selectedCheck && (
              <>
                {/* Report Header */}
                <div className="bg-blue-900 text-white p-6 rounded-t-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-blue-900" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">Vehicle Check Report</h2>
                        <p className="text-blue-200 text-sm">Walk-Around Inspection</p>
                      </div>
                    </div>
                    {selectedCheck.has_issues ? (
                      <Badge className="bg-red-500 text-white text-sm px-3 py-1">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        ISSUES FOUND
                      </Badge>
                    ) : (
                      <Badge className="bg-green-500 text-white text-sm px-3 py-1">
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        ALL CLEAR
                      </Badge>
                    )}
                  </div>

                  {/* Driver & Vehicle Info */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-blue-800/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-yellow-500" />
                        <span className="text-blue-200">Driver</span>
                      </div>
                      <p className="font-semibold text-lg">{selectedCheck.driver_name}</p>
                      <p className="text-blue-200 font-mono">Badge #{selectedCheck.driver_badge}</p>
                    </div>
                    <div className="bg-blue-800/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Bus className="w-4 h-4 text-yellow-500" />
                        <span className="text-blue-200">Vehicle</span>
                      </div>
                      <p className="font-semibold text-lg font-mono">{selectedCheck.vehicle_registration}</p>
                      <p className="text-blue-200 capitalize">{selectedCheck.vehicle_type}</p>
                    </div>
                  </div>

                  {/* Submission Time */}
                  <div className="flex items-center gap-2 mt-4 text-blue-200">
                    <Clock className="w-4 h-4" />
                    <span>Submitted: {formatDateLong(selectedCheck.submitted_at)}</span>
                  </div>
                </div>

                {/* Report Body */}
                <ScrollArea className="max-h-[50vh]">
                  <div className="p-6">
                    {/* Summary Stats */}
                    {(() => {
                      const summary = getChecklistSummary(selectedCheck.checklist);
                      return (
                        <div className="grid grid-cols-3 gap-4 mb-6">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                            <div className="text-3xl font-bold text-green-600">{summary.pass}</div>
                            <div className="text-sm text-green-700 font-medium">Passed</div>
                          </div>
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                            <div className="text-3xl font-bold text-red-600">{summary.fail}</div>
                            <div className="text-sm text-red-700 font-medium">Failed</div>
                          </div>
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
                            <div className="text-3xl font-bold text-slate-600">{summary.na}</div>
                            <div className="text-sm text-slate-700 font-medium">N/A</div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Full Checklist */}
                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Complete Checklist ({selectedCheck.checklist.length} Items)
                      </h3>
                      <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-slate-50">
                              <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm">#</th>
                              <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm">Check Item</th>
                              <th className="text-center py-3 px-4 font-semibold text-slate-700 text-sm">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedCheck.checklist.map((item, idx) => (
                              <tr 
                                key={idx} 
                                className={`border-t border-slate-100 ${
                                  item.status === 'fail' ? 'bg-red-50' : ''
                                }`}
                                data-testid={`checklist-row-${idx}`}
                              >
                                <td className="py-3 px-4 text-slate-500 font-mono text-sm">{idx + 1}</td>
                                <td className="py-3 px-4">
                                  <div className="flex items-center gap-3">
                                    {getStatusIcon(item.status)}
                                    <span className={`font-medium ${item.status === 'fail' ? 'text-red-800' : 'text-slate-800'}`}>
                                      {item.item}
                                    </span>
                                  </div>
                                </td>
                                <td className="py-3 px-4 text-center">
                                  {getStatusBadge(item.status)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Defects Section */}
                    {selectedCheck.defects && (
                      <div className="mb-6">
                        <h3 className="text-lg font-bold text-red-700 mb-3 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5" />
                          Defects Reported
                        </h3>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-red-800 whitespace-pre-wrap">{selectedCheck.defects}</p>
                        </div>
                      </div>
                    )}

                    {/* Comments Section */}
                    {selectedCheck.comments && (
                      <div className="mb-6">
                        <h3 className="text-lg font-bold text-slate-700 mb-3 flex items-center gap-2">
                          <FileText className="w-5 h-5" />
                          Additional Comments
                        </h3>
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                          <p className="text-slate-700 whitespace-pre-wrap">{selectedCheck.comments}</p>
                        </div>
                      </div>
                    )}

                    {/* No defects/comments note */}
                    {!selectedCheck.defects && !selectedCheck.comments && (
                      <div className="text-center text-slate-500 py-4">
                        No additional defects or comments reported
                      </div>
                    )}
                  </div>
                </ScrollArea>

                {/* Footer */}
                <div className="border-t border-slate-200 p-4 bg-slate-50 rounded-b-lg">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-slate-500">
                      Report ID: <span className="font-mono">{selectedCheck.id.slice(0, 8)}...</span>
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setShowFullReport(false)}
                    >
                      Close Report
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
